<template>
  <v-dialog v-model="isOpen" scrollable @click:outside="isOpen = false">
    <v-card v-if="isLoading" color="primary" dark>
      <v-card-text>
        {{ $t('global.loading') }}...
        <v-progress-linear indeterminate color="white" class="mb-0" />
      </v-card-text>
    </v-card>

    <v-card v-else-if="error" color="primary" dark>
      <div class="pa-3 d-flex align-center justify-center">
        <DisplayError
          :error="error"
          retriable
          @retry="fetch({ userId: $route.params.userId })"
        />
      </div>
    </v-card>

    <v-card v-else-if="!isLoaded" color="primary" dark />

    <v-card v-else>
      <v-card-title>
        {{
          $t(
            'pages.user.profile.earnsAndBurns.headers.completeBenefitsHistory',
            { program_name: programName }
          )
        }}

        <template v-if="cashbackFrozenReady">
          <v-spacer />
          <v-switch
            v-model="isCashbackFrozen"
            :disabled="isTogglingCashbackFrozen"
            :loading="isTogglingCashbackFrozen"
            :label="
              $t(
                isCashbackFrozen
                  ? 'pages.user.profile.earnsAndBurns.isCashbackFrozen.on'
                  : 'pages.user.profile.earnsAndBurns.isCashbackFrozen.off'
              )
            "
            :error-messages="togglingErrorMessages"
            :success-messages="togglingSuccessMessages"
            class="v-input--reverse"
          />
        </template>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-data-table
          :headers="HEADERS"
          :items="items"
          disable-pagination
          hide-default-footer
          :locale="$i18n.locale"
        >
          <template #header.amountEarned>
            {{ $t('pages.user.profile.earnsAndBurns.headers.amountEarned') }}
            <span class="error--text">
              <br />
              ({{
                $t('pages.user.profile.earnsAndBurns.headers.withoutCapping')
              }})
            </span>
          </template>

          <template #item.origin="{ item }">
            <BenefitModalItemOrigin :item="item" />
            <br v-if="item.origin_details && item.origin_details.length > 0" />
            <span v-if="item.origin_details && item.origin_details.length > 0">
              (
              <span v-for="(detail, index) in item.origin_details" :key="index">
                <BenefitModalItemDetail :detail="detail" />
                <span v-if="index !== item.origin_details.length - 1">,</span>
              </span>
              )
            </span>
          </template>
          <template #item.datetime="{ item }">
            <!-- TODO: update to vuetify 2.5 and remove this "span" for ".text-no-wrap" -->
            <span class="text-no-wrap">
              {{ item.datetime | momentFormat('human-long') }}
            </span>
          </template>
          <template #item.amountEarned="{ item }">
            <BenefitsTableValue
              v-if="item.amountEarned"
              :program-kind="programKind"
              :value="item.amountEarned"
            />
            <span v-if="item.amount_without_capping" class="error--text">
              <br />
              (
              <BenefitsTableValue
                :program-kind="programKind"
                :value="item.amount_without_capping"
              />
              )
            </span>
          </template>
          <template #item.amountBurned="{ item }">
            <BenefitsTableValue
              v-if="item.amountBurned"
              :program-kind="programKind"
              :value="item.amountBurned"
            />
          </template>
          <template #item.balance="{ item }">
            <BenefitsTableValue
              :program-kind="programKind"
              :value="item.balance"
            />
          </template>

          <template v-if="programCachedBenefits" #foot>
            <tfoot>
              <tr>
                <td :colspan="COLUMN_COUNT" />
              </tr>
              <tr>
                <td :colspan="COLUMN_COUNT - 1">
                  {{ $t('pages.user.profile.earnsAndBurns.displayedInApp') }}
                </td>
                <td class="text-right">
                  <BenefitsTableValue
                    :program-kind="programKind"
                    :value="programCachedBenefits.cached_current_benefits"
                  />
                </td>
              </tr>
              <tr v-show="isCacheOutdated">
                <td :colspan="COLUMN_COUNT - 3">
                  {{
                    $t(
                      'pages.user.profile.earnsAndBurns.displayedInAppCanDifferBecauseItIsCached'
                    )
                  }}
                </td>
                <td :colspan="3" class="text-right">
                  <v-btn
                    color="primary"
                    :loading="isSynchronizingCachedBenefits"
                    @click="synchronizeCachedBenefits"
                  >
                    {{
                      $t(
                        'pages.user.profile.earnsAndBurns.synchronizeCachedBenefits'
                      )
                    }}
                  </v-btn>
                </td>
              </tr>
            </tfoot>
          </template>
        </v-data-table>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn color="blue darken-1" text @click="isOpen = false">
          {{ $t('global.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import BigNumber from 'bignumber.js'

import BenefitsTableValue from './BenefitsTableValue'
import BenefitModalItemOrigin from './BenefitModalItemOrigin'
import BenefitModalItemDetail from './BenefitModalItemDetail'
import DisplayError from '~/components/layouts/DisplayError'
import { fetchCoreFromComponentAndHandleError } from '~/utils/functions/fetch-and-handle-errors'

const KEYS = ['origin', 'datetime', 'amountEarned', 'amountBurned', 'balance']
const COLUMN_COUNT = KEYS.length
const KEYS_ALIGNED_END = ['amountEarned', 'amountBurned', 'balance']
export default {
  components: {
    BenefitsTableValue,
    BenefitModalItemOrigin,
    BenefitModalItemDetail,
    DisplayError
  },
  props: {
    programKind: {
      type: String,
      required: true
    },
    programName: {
      type: String,
      required: true
    },
    programIdentifier: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      COLUMN_COUNT,

      isOpen: true,
      HEADERS: KEYS.map((key) => ({
        value: key,
        text: this.$t(`pages.user.profile.earnsAndBurns.headers.${key}`),
        align: KEYS_ALIGNED_END.includes(key) ? 'end' : 'start',
        cellClass: key === 'datetime' ? 'text-no-wrap' : null
      })),

      isLoadingCashbackFrozen: false,
      isLoadedCashbackFrozen: false,

      isCashbackFrozen: null,

      isTogglingCashbackFrozen: false,
      togglingCashbackFrozenSuccess: false,
      togglingCashbackFrozenError: null,

      isSynchronizingCachedBenefits: false
    }
  },
  computed: {
    ...mapState('user/profile/earnsAndBurns', [
      'isLoading',
      'isLoaded',
      'error',
      'earnsAndBurns',
      'cachedBenefits'
    ]),
    ...mapGetters('user', ['routeBasePath']),

    showCashbackFrozen() {
      return this.programName === 'Scanobar'
    },
    cashbackFrozenReady() {
      return (
        this.showCashbackFrozen &&
        this.isLoadedCashbackFrozen &&
        this.isCashbackFrozen !== null
      )
    },
    togglingSuccessMessages() {
      return this.togglingCashbackFrozenSuccess
        ? [this.$t('pages.user.profile.earnsAndBurns.isCashbackFrozen.updated')]
        : []
    },
    togglingErrorMessages() {
      return this.togglingCashbackFrozenError
        ? [this.$t('pages.user.profile.earnsAndBurns.isCashbackFrozen.error')]
        : []
    },

    programEarnsAndBurns() {
      if (!this.earnsAndBurns) return []

      return this.earnsAndBurns[this.programIdentifier] || []
    },
    items() {
      let balance = new BigNumber(0)
      return this.programEarnsAndBurns.map((earnOrBurn) => {
        const amount = new BigNumber(earnOrBurn.amount)
        balance = balance.plus(amount)

        let originLinkUrl
        let originLinkTo
        if (earnOrBurn.origin_record) {
          switch (earnOrBurn.origin_record.kind) {
            case 'declaration':
              originLinkTo = `${this.routeBasePath}/declarations/${earnOrBurn.origin_record.identifier}`
              break
          }
        }

        return {
          ...earnOrBurn,
          amountEarned: earnOrBurn.kind === 'earn' ? amount : null,
          amountBurned: earnOrBurn.kind === 'burn' ? amount : null,
          originLinkTo,
          originLinkUrl,
          balance
        }
      })
    },
    itemsCount() {
      return this.items.length
    },
    lastItem() {
      return this.itemsCount > 0 ? this.items[this.itemsCount - 1] : null
    },
    currentBalance() {
      return this.lastItem && this.lastItem.balance
    },

    programCachedBenefits() {
      if (!this.lastItem) return

      return this.cachedBenefits[this.lastItem.card_identifier]
    },

    isCacheOutdated() {
      if (!this.programCachedBenefits) return false
      if (!this.currentBalance) return false

      return !this.currentBalance.isEqualTo(
        this.programCachedBenefits.cached_current_benefits
      )
    }
  },
  watch: {
    isOpen(val) {
      if (!val) {
        this.$emit('close')
      }
    },
    isCashbackFrozen(_newValue, oldValue) {
      if (oldValue === null) return

      this.toggleCashbackFrozenStatus()
    }
  },
  mounted() {
    if (this.showCashbackFrozen) {
      this.fetchCashbackFrozenStatus()
    }
  },
  methods: {
    ...mapMutations('snackbar', ['addToast']),
    ...mapMutations('user/profile/earnsAndBurns', [
      'updateProgramCachedBenefits'
    ]),
    ...mapActions('user/profile/earnsAndBurns', ['fetch']),

    fetchCashbackFrozenStatus() {
      return fetchCoreFromComponentAndHandleError({
        $vm: this,
        isLoadedKey: 'isLoadedCashbackFrozen',
        isLoadingKey: 'isLoadingCashbackFrozen',
        errorInToast: true,

        axios: {
          url: `/users/${this.$route.params.userId}/programs/${this.programIdentifier}/get_cashback_frozen_status`
        },

        onSuccess: (result) => {
          this.isCashbackFrozen = result.is_cashback_frozen
        }
      })
    },

    toggleCashbackFrozenStatus() {
      return fetchCoreFromComponentAndHandleError({
        $vm: this,
        isLoadingKey: 'isTogglingCashbackFrozen',
        errorKey: 'togglingCashbackFrozenError',

        axios: {
          method: 'POST',
          url: `/users/${this.$route.params.userId}/programs/${this.programIdentifier}/toggle_cashback_frozen_status`
        },

        onSuccess: (result) => {
          this.isCashbackFrozen = result.is_cashback_frozen

          this.togglingCashbackFrozenSuccess = true
          setTimeout(() => {
            this.togglingCashbackFrozenSuccess = false
          }, 1000)
        },
        onError: () => {
          this.isCashbackFrozen = !this.isCashbackFrozen
        }
      })
    },

    synchronizeCachedBenefits() {
      return fetchCoreFromComponentAndHandleError({
        $vm: this,
        isLoadedKey: 'isSynchronizingCachedBenefits',
        isLoadingKey: 'isSynchronizingCachedBenefits',
        errorInToast: true,

        axios: {
          method: 'POST',
          url: `/users/${this.$route.params.userId}/programs/${this.programIdentifier}/synchronize_cached_benefits`
        },

        onSuccess: (result) => {
          this.updateProgramCachedBenefits({
            identifier: this.lastItem.card_identifier,
            benefits: result
          })
        }
      })
    }
  }
}
</script>

<style lang="scss">
// https://github.com/vuetifyjs/vuetify/issues/7283

// Reversed input variant
.v-input--reverse .v-input__slot {
  flex-direction: row-reverse;
  justify-content: flex-end;
  .v-application--is-ltr & {
    .v-input--selection-controls__input {
      margin-right: 0;
      margin-left: 8px;
    }
  }
  .v-application--is-rtl & {
    .v-input--selection-controls__input {
      margin-left: 0;
      margin-right: 8px;
    }
  }
}

// Bonus "expand" variant
.v-input--expand .v-input__slot {
  // justify-content: space-between;
  .v-label {
    display: block;
    flex: 1;
  }
}
</style>
